import React, { Component, createRef } from 'react';
import { string, arrayOf, func, shape, bool, number } from 'prop-types';

import { stringOrNumberPropTypes } from '~utils/propTypes';
import { isMobile as checkIsMobile } from '~constants/sizes';

import { moveOptions } from './utils';
import { KEY_CODES, MOVE_OPTIONS, MAX_STRING_LENGTH, MOUSE_EVENTS } from './constants';
import Dropdown from './layout';

class DropdownContainer extends Component {
  static getDerivedStateFromProps = (props, state) => {
    if (props.value !== state.selected.value) {
      const { value: newValue, options } = props;
      if (!options) {
        return null;
      }
      const { value, name: text } = options.find(o => o.value === newValue) || options[0];
      return { currentFocus: value, selected: { value, text } };
    }

    return null;
  };

  state = {
    selected: {
      value: '',
      text: ''
    },
    currentFocus: this.props.value,
    isOpen: false
  };

  componentDidMount() {
    const { placeholder, value, options } = this.props;
    const firstOption = options.find(o => o.value === value) || options[0];
    const name = value ? firstOption.name : placeholder || firstOption.name;

    if (!this.shouldShowOptions() || !name || name === '') {
      this.setCurrentFocus(value);
    }

    this.setSelected(value, name);
  }

  setSelected = (value, text) => {
    const { setFieldValue, name } = this.props;

    setFieldValue?.(name, value);

    this.setState({ selected: { value, text } });
  };

  setCurrentFocus = value => this.setState({ currentFocus: value });

  setIsOpen = value => this.setState({ isOpen: value });

  handleKeyDown = event => {
    event.preventDefault();
    const { keyCode, target } = event;
    const { isOpen, currentFocus } = this.state;
    const { options } = this.props;
    const maxArrayIndex = options.length - 1;
    let currentIndexFocus = options.findIndex(op => op.value === currentFocus);

    if (isOpen) {
      if (keyCode === KEY_CODES.arrowDown) {
        currentIndexFocus = moveOptions(MOVE_OPTIONS.next, currentIndexFocus, 1, maxArrayIndex);
      } else if (keyCode === KEY_CODES.arrowUp) {
        currentIndexFocus = moveOptions(MOVE_OPTIONS.prev, currentIndexFocus, 1, maxArrayIndex);
      } else if (keyCode === KEY_CODES.enter || keyCode === KEY_CODES.spacebar) {
        target.querySelector(`[value="${currentFocus}"]`).click();
      } else if (keyCode === KEY_CODES.esc) {
        this.setIsOpen(false);
      }
      this.setCurrentFocus(options[currentIndexFocus]?.value);
    }
  };

  handleOnBlur = () => this.setIsOpen(false);

  isMobile = checkIsMobile();

  shouldShowOptions = () => {
    const { options, disabled, allowUniqueOption } = this.props;
    const areOptionsAvailable = options?.length > 1;
    return !disabled && (allowUniqueOption || areOptionsAvailable);
  };

  handleShowOptions = () => {
    const { isOpen, selected } = this.state;
    if (this.isMobile) {
      const element = this.inputRef.current;
      if (element) {
        if (document.createEvent) {
          const e = document.createEvent(MOUSE_EVENTS.createEventName);
          e.initMouseEvent(MOUSE_EVENTS.clickConfig);
          element.dispatchEvent(e);
        } else if (element.fireEvent) {
          // IE - Edge probably
          element.fireEvent(MOUSE_EVENTS.onMouseDown);
        }
      }
    } else {
      this.setIsOpen(!isOpen);
    }
    this.setCurrentFocus(selected.value);
  };

  handleSelectionOption = event => {
    const { onChange, options } = this.props;
    const { target } = event;
    const selected = options.find(
      option => `${option.value}` === `${target.value}` || `${option.name}` === `${target.dataset.text}`
    );
    if (selected) {
      const { value, name } = selected;
      this.setSelected(value, name);
      this.setCurrentFocus(value);
      this.setIsOpen(false);
      onChange({ target: { value } });
    }
  };

  inputRef = createRef();

  render() {
    const {
      label,
      placeholder,
      name,
      options,
      className,
      children,
      allowUniqueOption,
      labelClassname,
      optionContainerClassName,
      maxOptionLength,
      maxLabelLength,
      labelIcon,
      ariaLabel,
      error,
      id
    } = this.props;

    return (
      <Dropdown
        label={label}
        placeholder={placeholder}
        name={name}
        options={options}
        className={className}
        labelClassname={labelClassname}
        selected={this.state.selected}
        currentFocus={this.state.currentFocus}
        isOpen={this.state.isOpen}
        showOptions={this.shouldShowOptions()}
        onSelectionOption={this.handleSelectionOption}
        onShowOptions={this.handleShowOptions}
        onHandledKeyDown={this.handleKeyDown}
        onHandleBlur={this.handleOnBlur}
        customChildren={children}
        labelIcon={labelIcon}
        allowUniqueOption={allowUniqueOption}
        optionContainerClassName={optionContainerClassName}
        maxOptionLength={maxOptionLength}
        maxLabelLength={maxLabelLength}
        error={error}
        inputRef={this.inputRef}
        isMobile={this.isMobile}
        ariaLabel={ariaLabel}
        id={id}
      />
    );
  }
}

DropdownContainer.propTypes = {
  name: string.isRequired,
  options: arrayOf(
    shape({
      name: string.isRequired,
      value: stringOrNumberPropTypes
    })
  ).isRequired,
  onChange: func.isRequired,
  allowUniqueOption: bool,
  ariaLabel: string,
  className: string,
  disabled: bool,
  error: string,
  id: string,
  label: string,
  labelClassname: string,
  labelIcon: string,
  maxLabelLength: number,
  maxOptionLength: number,
  optionContainerClassName: string,
  placeholder: string,
  setFieldValue: func,
  value: stringOrNumberPropTypes
};

DropdownContainer.defaultProps = {
  className: '',
  id: undefined,
  maxLabelLength: MAX_STRING_LENGTH.label,
  maxOptionLength: MAX_STRING_LENGTH.option
};

export default DropdownContainer;
